import {
  CARBON_MARKER,
  EXTRACTION_METHOD_LABELS,
  REACTIVITY_LADDER,
  extractionMetalBySymbol,
} from './materialsChemistryExtraction';
import {
  NANOSCALE_MAX_NM,
  NANOSCALE_MIN_NM,
  VISIBLE_MAX_NM,
  VISIBLE_MIN_NM,
  sizeClass,
} from './materialsChemistryNano';

export function ReactivityLadder({ metal, revealed }: { metal: string; revealed: boolean }) {
  const selected = extractionMetalBySymbol(metal);
  return (
    <figure className="min-w-0" aria-label="Reactivity series with carbon">
      <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">Most reactive</p>
      <ol className="mt-2 grid gap-1">
        {REACTIVITY_LADDER.map((symbol) => {
          if (symbol === CARBON_MARKER) {
            return (
              <li
                key={symbol}
                className="flex items-center gap-2 border-y-2 border-dashed border-brand-coral/60 py-1 text-xs font-extrabold text-brand-coral"
              >
                C · carbon — reduces oxides of metals below this line
              </li>
            );
          }
          const isSelected = symbol === metal;
          return (
            <li
              key={symbol}
              aria-current={isSelected ? 'true' : undefined}
              className={`flex items-center justify-between gap-2 rounded-xl px-3 py-1 text-sm ${
                isSelected ? 'border-2 border-ink bg-wash-sky font-extrabold text-ink' : 'border border-hairline bg-white font-bold text-ink-soft'
              }`}
            >
              <span>
                {isSelected ? '▶ ' : ''}
                {symbol}
              </span>
              {isSelected && revealed && selected ? (
                <span className="text-right text-[0.7rem] font-extrabold text-ink-soft">
                  {selected.accepted.map((method) => EXTRACTION_METHOD_LABELS[method]).join(' or ')}
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
      <p className="mt-2 text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">Least reactive</p>
      <figcaption className="mt-2 text-xs font-medium leading-5 text-slate2">
        Reactivity series with carbon’s position for reduction at high temperature. The selected metal is
        outlined and marked ▶.
      </figcaption>
    </figure>
  );
}

const SCALE_MIN_EXP = -1;
const SCALE_MAX_EXP = 4;
const SCALE_TICKS: { nm: number; label: string }[] = [
  { nm: 0.1, label: '0.1 nm' },
  { nm: 1, label: '1 nm' },
  { nm: 10, label: '10 nm' },
  { nm: 100, label: '100 nm' },
  { nm: 1000, label: '1 µm' },
  { nm: 10000, label: '10 µm' },
];
const PERCENT = 100;

function logPercent(nm: number): number {
  const exponent = Math.log10(nm);
  const clamped = Math.min(Math.max(exponent, SCALE_MIN_EXP), SCALE_MAX_EXP);
  return ((clamped - SCALE_MIN_EXP) / (SCALE_MAX_EXP - SCALE_MIN_EXP)) * PERCENT;
}

const SIZE_CLASS_LABELS = {
  'below-nanoscale': 'below the nanoscale',
  nanoscale: 'inside the nanoscale',
  'above-nanoscale': 'above the nanoscale',
} as const;

export function SizeScale({ sizeNm, label, revealed }: { sizeNm: number; label: string; revealed: boolean }) {
  const classification = sizeClass(sizeNm);
  return (
    <figure className="min-w-0">
      <div
        className="relative mt-6 h-10 rounded-full bg-surface"
        role="img"
        aria-label={`Logarithmic size scale from 0.1 nm to 10 µm. Nanoscale band 1–100 nm; visible light wavelengths 400–700 nm.${
          revealed && classification ? ` ${label}: ${sizeNm} nm, ${SIZE_CLASS_LABELS[classification]}.` : ''
        }`}
      >
        <span
          className="absolute inset-y-0 rounded-full bg-wash-mint ring-1 ring-brand-mint/60"
          style={{ left: `${logPercent(NANOSCALE_MIN_NM)}%`, width: `${logPercent(NANOSCALE_MAX_NM) - logPercent(NANOSCALE_MIN_NM)}%` }}
        />
        <span
          className="absolute inset-y-2 rounded-full bg-wash-sunshine ring-1 ring-brand-sunshine/60"
          style={{ left: `${logPercent(VISIBLE_MIN_NM)}%`, width: `${logPercent(VISIBLE_MAX_NM) - logPercent(VISIBLE_MIN_NM)}%` }}
        />
        {revealed ? (
          <span
            className="absolute -top-6 -translate-x-1/2 whitespace-nowrap text-[0.65rem] font-extrabold text-ink"
            style={{ left: `${logPercent(sizeNm)}%` }}
          >
            ▼ {label}
          </span>
        ) : null}
      </div>
      <div className="relative mt-1 h-4 text-[0.6rem] font-bold text-slate2" aria-hidden="true">
        {SCALE_TICKS.map((tick) => (
          <span key={tick.nm} className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${logPercent(tick.nm)}%` }}>
            {tick.label}
          </span>
        ))}
      </div>
      <figcaption className="mt-3 flex flex-wrap gap-3 text-xs font-bold text-slate2">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-5 rounded-full bg-wash-mint ring-1 ring-brand-mint/60" aria-hidden="true" /> Nanoscale 1–100 nm
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-5 rounded-full bg-wash-sunshine ring-1 ring-brand-sunshine/60" aria-hidden="true" /> Visible light 400–700 nm
        </span>
        <span>Log scale: each step is 10 ×.</span>
      </figcaption>
    </figure>
  );
}

const SUBDIVISION_VIEW = 160;
const SUBDIVISION_PAD = 10;
const MAX_DRAWN_DIVISIONS = 10;

export function SubdivisionSquare({ perEdge, caption }: { perEdge: number; caption: string }) {
  const drawn = Math.min(Math.max(Math.round(perEdge), 1), MAX_DRAWN_DIVISIONS);
  const size = SUBDIVISION_VIEW - 2 * SUBDIVISION_PAD;
  const step = size / drawn;
  return (
    <figure className="mx-auto min-w-0 max-w-[14rem]">
      <svg viewBox={`0 0 ${SUBDIVISION_VIEW} ${SUBDIVISION_VIEW}`} className="w-full" role="img" aria-label={`Face of a cube. ${caption}.`}>
        <rect x={SUBDIVISION_PAD} y={SUBDIVISION_PAD} width={size} height={size} className="fill-wash-sky stroke-brand-sky" strokeWidth="2" />
        {Array.from({ length: drawn - 1 }, (_, index) => {
          const offset = SUBDIVISION_PAD + step * (index + 1);
          return (
            <g key={index} className="stroke-brand-sky" strokeWidth="1">
              <line x1={offset} x2={offset} y1={SUBDIVISION_PAD} y2={SUBDIVISION_PAD + size} />
              <line y1={offset} y2={offset} x1={SUBDIVISION_PAD} x2={SUBDIVISION_PAD + size} />
            </g>
          );
        })}
      </svg>
      <figcaption className="mt-2 text-center text-xs font-medium leading-5 text-slate2">{caption}</figcaption>
    </figure>
  );
}
